import {useEffect, useRef, useState} from "react";
import {Outlet, useNavigate} from "react-router-dom";
import AuthCheck from "../component/auth.check.tsx";
import Player from "../component/player.tsx";
import type {DestroyFn} from "../../modules/interfaces.ts";
import {app, callCustomerCenter, onair} from "../../modules/config.ts";
import {askConfirm} from "../../stores/dialog.thunk.ts";
import {useAppDispatch} from "../../stores/hook.ts";
import ModalBottom from "../component/modal.bottom.tsx";
import {Api} from "../../modules/api.ts";


function PageMain() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [menu, setMenu] = useState('channel');

  const scrollerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLHeadElement>(null);

  useEffect(() => {
    const setupDropDown = setupDropdownToggle('.icon-wrap', '.dropdown-layer');

    // const scrollArea = document.querySelector('.scroll-area') as HTMLElement | null;
    // const header = document.querySelector('header') as HTMLElement | null;

    window.addEventListener('resize', setupScrollAreaHeight);
    setupScrollAreaHeight();

    let setupScrollDirection: DestroyFn;
    if (scrollerRef.current && headerRef.current) {
      setupScrollDirection = setupScrollDirectionToggle(5);
    }

    return () => {
      setupDropDown();
      window.removeEventListener('resize', setupScrollAreaHeight);
      if (setupScrollDirection) setupScrollDirection();
    };
  }, []);

  /**
   * 하단 메뉴 클릭
   * @param menu
   */
  function menuOpenHandler(menu: string): void {
    switch (menu) {
      case 'announce':
      case 'ai-voice':
        setMenu(menu);
        navigate(`/main/${menu}`);
        break;
      default:
        setMenu('channel');
        navigate('/main')
        break;
    }
  }

  /**
   * 로그아웃 클릭
   */
  async function logoutHandler(): Promise<void> {
    const isConfirmed = await dispatch(askConfirm('', `로그아웃하시겠습니까?`));
    if (isConfirmed) {
      onair.clear().then(async () => {
        await Api.logout('logout').then(() => {
          navigate('/login', {
            state: {
              logout: true
            },
            replace: true
          });
        });
      });
    }
  }

  /**
   * 윈도우 리사이징시 스크롤 크기 변경
   */
  const setupScrollAreaHeight = () => {
    // 모바일 브라우저 URL바 포함 100vh 오차 보정용 --vh 업데이트
    // scroll-area 높이는 CSS position:absolute의 top + bottom으로 자동 계산됨
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  /**
   * 스크롤 처리 부분
   * @param delta
   */
  function setupScrollDirectionToggle(delta: number = 5): DestroyFn {
    let lastScrollTop = 0;

    const scrollArea = scrollerRef.current;
    const header = headerRef.current;

    if(scrollArea && header) {
      const onScroll = () => {
        const scrollTop = scrollArea.scrollTop;
        // 맨 위면 header 보이고 scroll-area 다시 아래로
        if (scrollTop <= 1) {
          header.classList.remove("nav-up");
          header.classList.add("nav-down");
          scrollArea.classList.remove("header-hidden");
          lastScrollTop = scrollTop;
          return;
        }

        // 아래로 스크롤할 때 header 숨김
        if (scrollTop > lastScrollTop + delta) {
          header.classList.remove("nav-down");
          header.classList.add("nav-up");
          scrollArea.classList.add("header-hidden");
        }

        // 위로 스크롤할 땐 변화 없음
        lastScrollTop = scrollTop;
      };

      scrollArea.addEventListener('scroll', onScroll);
      return () => {
        scrollArea.removeEventListener('scroll', onScroll);
      };
    } else {
      return () => {};
    }
  }

  /**
   * 드랍다운 토글
   * @param iconSelector
   * @param dropdownSelector
   */
  function setupDropdownToggle(iconSelector: string, dropdownSelector: string): DestroyFn {
    const iconWrap = document.querySelector(iconSelector) as HTMLElement | null;
    const dropdown = document.querySelector(dropdownSelector) as HTMLElement | null;

    if (!iconWrap || !dropdown) {
      return () => {
      };
    }

    const onIconClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dropdown.style.display = (dropdown.style.display === "none" || dropdown.style.display === "") ? "block" : "none";
    };

    const onDocumentClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!iconWrap.contains(target) && !dropdown.contains(target)) {
        dropdown.style.display = "none";
      }
    };

    iconWrap.addEventListener('click', onIconClick);
    document.addEventListener('click', onDocumentClick);

    return () => {
      iconWrap.removeEventListener('click', onIconClick);
      document.removeEventListener('click', onDocumentClick);
    };
  }

  return (
    <AuthCheck>
      <div className="container">
        {/* 헤더 */}
        <header className="nav-down" ref={headerRef}>
          <div className="header-inner">
            <div className="name-wrap">
              <h2>{app.getClientName()}</h2>
              <h3>{app.getClientPeriod()}</h3>
            </div>
            <div className="dropdown-wrap">
              <div className="icon-wrap">
                <a><img src="/images/icon-menu.png" alt=""/></a>
              </div>
              <div className="dropdown-layer">
                <ul>
                  <li className="app-version" style={{fontSize: '12px', color: '#909090', justifyContent: 'flex-end'}} dangerouslySetInnerHTML={{__html: `${app.os}${app.os.includes("Windows") ? '<br />' : '.'}${app.app_version}`}} />
                  <li><a onClick={callCustomerCenter}><img src="/images/icon-drop1.png" alt=""/>{app.csphone} 고객센터 연결</a></li>
                  <li><a onClick={logoutHandler}><img src="/images/icon-drop2.png" alt=""/>로그아웃</a></li>
                </ul>
              </div>
            </div>
          </div>
        </header>

        {/* 헤더 */}
        <div className="scroll-area" ref={scrollerRef}>
          <Player/>
          <Outlet/>
        </div>

        <ModalBottom />

        <nav className="bottom-nav">
          <div className="nav-inner">
            <ul className="nav-list">
              <li className={`nav-item ${menu === 'channel' ? 'on' : ''}`}>
                <a onClick={() => menuOpenHandler('channel')}>
                  <img src={(menu === 'channel' ? `${app.imagePath}/icon-channel-on.png` : '/images/icon-channel.png')} alt=""/>
                  <span>채널</span>
                </a>
              </li>
              <li className={`nav-item ${menu === 'announce' ? 'on' : ''}`}>
                <a onClick={() => menuOpenHandler('announce')}>
                  <img src={(menu === 'announce' ? `${app.imagePath}/icon-announce-on.png` : '/images/icon-announce.png')} alt=""/>
                  <span>안내방송</span>
                </a>
              </li>
              <li className={`nav-item ${menu === 'ai-voice' ? 'on' : ''}`}>
                <a onClick={() => menuOpenHandler('ai-voice')}>
                  <img src={(menu === 'ai-voice' ? `${app.imagePath}/icon-ai-on.png` : '/images/icon-ai.png')} alt=""/>
                  <span>AI음성</span>
                </a>
              </li>
            </ul>
          </div>
        </nav>


      </div>
    </AuthCheck>
  );
}

export default PageMain;